/**
 * 详情页模式管理 Hook
 *
 * 管理详情页的 create / edit / view 三态切换、表单提交处理。
 * 与 useDetailPage（数据获取）互补，专注于页面交互逻辑。
 */

import { useCallback, useMemo, useState } from 'react';
import type { ApiResponse } from '../types/api';
import { handleOperationError } from './shared-async';
import { useLatestRef } from './useLatestRef';

/**
 * 页面模式
 */
export type PageMode = 'create' | 'edit' | 'view';

/**
 * DetailPageMode 文案配置（支持国际化）
 */
export interface DetailPageModeMessages {
  /** 创建成功提示，默认 'Created successfully' */
  createSuccess: string;
  /** 更新成功提示，默认 'Updated successfully' */
  updateSuccess: string;
  /** 操作失败提示，默认 'Operation failed' */
  operationFailed: string;
  /** 缺少 API 函数错误提示，默认 'Config error: missing API function' */
  missingApi: string;
}

const DEFAULT_DETAIL_PAGE_MESSAGES: DetailPageModeMessages = {
  createSuccess: 'Created successfully',
  updateSuccess: 'Updated successfully',
  operationFailed: 'Operation failed',
  missingApi: 'Config error: missing API function',
};

/**
 * useDetailPageMode 配置
 */
export interface UseDetailPageModeOptions<T = Record<string, unknown>> {
  /** 当前记录 ID（有 ID 且非 create 模式时为编辑/查看模式） */
  id?: string | number;
  /** 创建 API */
  createApi?: (data: T) => Promise<ApiResponse<unknown>>;
  /** 更新 API */
  updateApi?: (id: string | number, data: T) => Promise<ApiResponse<unknown>>;
  /** 成功状态码，默认 '200' */
  successCode?: string | number;
  /** 初始模式，不传则根据 id 自动判断 */
  initialMode?: PageMode;
  /** 导航函数（返回上一页） */
  goBack?: () => void;
  /** 消息展示函数 */
  showMessage?: (content: string, type: 'success' | 'error') => void;
  /** 自定义文案（支持国际化） */
  messages?: Partial<DetailPageModeMessages>;
  /** 提交成功回调 */
  onSuccess?: (mode: PageMode, data: unknown) => void;
  /** 提交失败回调 */
  onError?: (error: Error) => void;
}

/**
 * useDetailPageMode 返回值
 */
export interface UseDetailPageModeReturn<T = Record<string, unknown>> {
  /** 当前页面模式 */
  mode: PageMode;
  /** 是否为创建模式 */
  isCreate: boolean;
  /** 是否为编辑模式 */
  isEdit: boolean;
  /** 是否为查看模式 */
  isView: boolean;
  /** 表单是否只读 */
  readonly: boolean;
  /** 切换模式 */
  setMode: (mode: PageMode) => void;
  /** 从查看切换到编辑 */
  switchToEdit: () => void;
  /** 提交中状态 */
  submitting: boolean;
  /** 提交表单 */
  handleSubmit: (data: T) => Promise<boolean>;
  /** 返回上一页 */
  handleBack: () => void;
}

/**
 * 详情页模式管理 Hook
 *
 * @example
 * ```tsx
 * const {
 *   mode, isCreate, readonly, submitting,
 *   handleSubmit, handleBack, switchToEdit,
 * } = useDetailPageMode({
 *   id: params.id,
 *   createApi: createUser,
 *   updateApi: (id, data) => updateUser(id, data),
 *   goBack: () => history.push('/user/list'),
 *   showMessage: (content, type) => message[type](content),
 *   onSuccess: () => actionRef.current?.reload(),
 * });
 *
 * <ProForm
 *   readonly={readonly}
 *   loading={submitting}
 *   onFinish={handleSubmit}
 * />
 * ```
 */
export function useDetailPageMode<T = Record<string, unknown>>(
  options: UseDetailPageModeOptions<T>,
): UseDetailPageModeReturn<T> {
  const { id, successCode = '200' } = options;

  // 用 useLatestRef 稳定文案和回调引用，避免每次渲染重建对象导致 useCallback 失效
  const msgRef = useLatestRef<DetailPageModeMessages>({
    ...DEFAULT_DETAIL_PAGE_MESSAGES,
    ...options.messages,
  });

  const createApiRef = useLatestRef(options.createApi);
  const updateApiRef = useLatestRef(options.updateApi);
  const showMessageRef = useLatestRef(options.showMessage);
  const onSuccessRef = useLatestRef(options.onSuccess);
  const onErrorRef = useLatestRef(options.onError);
  const goBackRef = useLatestRef(options.goBack);

  // 自动推断初始模式
  const defaultMode: PageMode = options.initialMode ?? (id ? 'view' : 'create');
  const [mode, setMode] = useState<PageMode>(defaultMode);
  const [submitting, setSubmitting] = useState(false);

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const readonly = isView;

  const switchToEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const handleSubmit = useCallback(
    async (data: T): Promise<boolean> => {
      const msg = msgRef.current;
      setSubmitting(true);
      try {
        let response: ApiResponse<unknown>;

        if (isCreate && createApiRef.current) {
          response = await createApiRef.current(data);
        } else if (isEdit && updateApiRef.current && id) {
          response = await updateApiRef.current(id, data);
        } else {
          showMessageRef.current?.(msg.missingApi, 'error');
          return false;
        }

        const isSuccess = String(response.code) === String(successCode);
        if (isSuccess) {
          showMessageRef.current?.(
            isCreate ? msg.createSuccess : msg.updateSuccess,
            'success',
          );
          onSuccessRef.current?.(mode, response.data);
          return true;
        }

        showMessageRef.current?.(response.msg || msg.operationFailed, 'error');
        return false;
      } catch (error) {
        handleOperationError({
          error,
          fallbackMessage: msg.operationFailed,
          showError: (message) => showMessageRef.current?.(message, 'error'),
          onError: onErrorRef.current,
        });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [
      isCreate,
      isEdit,
      id,
      successCode,
      mode,
      createApiRef,
      msgRef,
      onErrorRef,
      onSuccessRef,
      showMessageRef,
      updateApiRef,
    ],
  );

  const handleBack = useCallback(() => {
    goBackRef.current?.();
  }, [goBackRef]);

  return useMemo(
    () => ({
      mode,
      isCreate,
      isEdit,
      isView,
      readonly,
      setMode,
      switchToEdit,
      submitting,
      handleSubmit,
      handleBack,
    }),
    [
      mode,
      isCreate,
      isEdit,
      isView,
      readonly,
      switchToEdit,
      submitting,
      handleSubmit,
      handleBack,
    ],
  );
}
